import type { SheetTable } from "./parse-delimited"
import { documentPreviewKind, formatFileSize } from "./kinds"
import { clampTable, parseDelimited } from "./parse-delimited"
import { parseDocxXml } from "./parse-docx"

export interface DocumentPreviewCopy {
	open: string
	download: string
	loading: string
	failed: string
	moreRows: (count: number) => string
	sheetOf: (name: string) => string
}

const DEFAULT_COPY: DocumentPreviewCopy = {
	open: "Open",
	download: "Download",
	loading: "Loading preview",
	failed: "This file could not be previewed. Download it to open it.",
	moreRows: (count) => `${count} more rows in the file`,
	sheetOf: (name) => `Sheet: ${name}`,
}

const READY = "data-file-embed-ready"

const MAX_PARSE_BYTES = 8 * 1024 * 1024

/**
 * Builds an inline preview for every document placeholder in `container`.
 *
 * Everything is parsed in the reader's own browser, so an attached document
 * never leaves for a third-party viewer.
 */
export function enhanceDocumentPreviews(
	container: HTMLElement,
	copy: Partial<DocumentPreviewCopy> = {},
): () => void {
	const text = { ...DEFAULT_COPY, ...copy }
	const controllers: AbortController[] = []

	const nodes = container.querySelectorAll<HTMLElement>(
		`[data-file-embed]:not([${READY}])`,
	)

	nodes.forEach((node) => {
		node.setAttribute(READY, "")
		const src = node.getAttribute("data-file-src") ?? ""
		const fileName = node.getAttribute("data-file-name") ?? "Document"
		const mimeType = node.getAttribute("data-file-type")
		const fileSize = Number(node.getAttribute("data-file-size")) || null
		if (!src) return

		const kind = documentPreviewKind({ fileName, mimeType })
		const controller = new AbortController()
		controllers.push(controller)

		node.replaceChildren(
			buildShell({ node, src, fileName, fileSize, text, kind }),
		)

		const body = node.querySelector<HTMLElement>("[data-file-embed-body]")
		if (!body) return

		if (kind === "none") {
			body.remove()
			return
		}

		if (kind === "pdf") {
			body.replaceChildren(buildPdfFrame(src, fileName))
			return
		}

		if (fileSize && fileSize > MAX_PARSE_BYTES) {
			body.remove()
			return
		}

		void loadTable({ src, kind, controller, fileName })
			.then((table) => {
				if (controller.signal.aborted) return
				body.replaceChildren(
					kind === "word"
						? buildDocument(table.rows, text)
						: buildTable(table, text),
				)
			})
			.catch(() => {
				if (controller.signal.aborted) return
				body.replaceChildren(buildNote(text.failed))
			})
	})

	return () => controllers.forEach((controller) => controller.abort())
}

function buildShell(args: {
	node: HTMLElement
	src: string
	fileName: string
	fileSize: number | null
	text: DocumentPreviewCopy
	kind: ReturnType<typeof documentPreviewKind>
}): DocumentFragment {
	const { src, fileName, fileSize, text, kind } = args
	const fragment = args.node.ownerDocument.createDocumentFragment()

	const header = el("div", "pl-doc-preview__header")
	const title = el("span", "pl-doc-preview__name")
	title.textContent = fileName
	header.append(title)

	const meta = el("span", "pl-doc-preview__meta")
	const size = formatFileSize(fileSize)
	const extension = fileName.includes(".")
		? (fileName.split(".").pop() ?? "").toUpperCase()
		: ""
	meta.textContent = [extension, size].filter(Boolean).join(" · ")
	header.append(meta)

	const link = args.node.ownerDocument.createElement("a")
	link.className = "pl-doc-preview__action"
	link.href = src
	link.target = "_blank"
	link.rel = "noopener noreferrer"
	link.textContent = kind === "none" ? text.download : text.open
	header.append(link)

	fragment.append(header)

	const body = el("div", "pl-doc-preview__body")
	body.setAttribute("data-file-embed-body", "")
	body.textContent = kind === "none" ? "" : text.loading
	fragment.append(body)

	return fragment
}

function buildPdfFrame(src: string, fileName: string): HTMLIFrameElement {
	const frame = document.createElement("iframe")
	frame.className = "pl-doc-preview__pdf"
	frame.src = src
	frame.title = fileName
	frame.setAttribute("loading", "lazy")
	return frame
}

async function loadTable(args: {
	src: string
	kind: ReturnType<typeof documentPreviewKind>
	controller: AbortController
	fileName: string
}): Promise<SheetTable> {
	const response = await fetch(args.src, { signal: args.controller.signal })
	if (!response.ok)
		throw new Error(`Fetching the file failed: ${response.status}`)

	if (args.kind === "csv") {
		return parseDelimited(await response.text())
	}

	if (args.kind === "sheet") {
		const [{ read, utils }, buffer] = await Promise.all([
			import("xlsx"),
			response.arrayBuffer(),
		])
		const workbook = read(buffer, { type: "array" })
		const name = workbook.SheetNames[0] ?? ""
		const sheet = name ? workbook.Sheets[name] : undefined
		if (!sheet) return { name: "", rows: [], truncatedRows: 0 }
		const rows = utils.sheet_to_json<string[]>(sheet, {
			header: 1,
			blankrows: false,
			defval: "",
			raw: false,
		})
		return clampTable({
			name: workbook.SheetNames.length > 1 ? name : "",
			rows: rows.map((row) => row.map((cell) => String(cell ?? ""))),
			truncatedRows: 0,
		})
	}

	const [{ unzipSync, strFromU8 }, buffer] = await Promise.all([
		import("fflate"),
		response.arrayBuffer(),
	])
	const files = unzipSync(new Uint8Array(buffer), {
		filter: (file) => file.name === "word/document.xml",
	})
	const xml = files["word/document.xml"]
	if (!xml) throw new Error("This document has no readable body")
	const blocks = parseDocxXml(strFromU8(xml))
	return {
		name: "",
		rows: blocks.map((block) => [
			block.type,
			String(block.level ?? ""),
			block.text,
		]),
		truncatedRows: 0,
	}
}

function buildTable(table: SheetTable, text: DocumentPreviewCopy): HTMLElement {
	const wrapper = el("div", "pl-doc-preview__scroll")

	if (table.name) wrapper.append(buildNote(text.sheetOf(table.name)))

	if (table.rows.length === 0) {
		wrapper.append(buildNote(text.failed))
		return wrapper
	}

	const tableEl = document.createElement("table")
	tableEl.className = "pl-doc-preview__table"

	const [headerRow, ...bodyRows] = table.rows
	const head = document.createElement("thead")
	const headTr = document.createElement("tr")
	;(headerRow ?? []).forEach((cell) => {
		const th = document.createElement("th")
		th.textContent = cell
		headTr.append(th)
	})
	head.append(headTr)
	tableEl.append(head)

	const body = document.createElement("tbody")
	bodyRows.forEach((row) => {
		const tr = document.createElement("tr")
		row.forEach((cell) => {
			const td = document.createElement("td")
			td.textContent = cell
			tr.append(td)
		})
		body.append(tr)
	})
	tableEl.append(body)
	wrapper.append(tableEl)

	if (table.truncatedRows > 0) {
		wrapper.append(buildNote(text.moreRows(table.truncatedRows)))
	}

	return wrapper
}

function buildDocument(
	rows: string[][],
	text: DocumentPreviewCopy,
): HTMLElement {
	const wrapper = el("div", "pl-doc-preview__scroll pl-doc-preview__document")
	if (rows.length === 0) {
		wrapper.append(buildNote(text.failed))
		return wrapper
	}

	let list: HTMLUListElement | null = null

	rows.forEach(([type, level, value]) => {
		const content = value ?? ""
		if (type === "listItem") {
			if (!list) {
				list = document.createElement("ul")
				wrapper.append(list)
			}
			const item = document.createElement("li")
			item.style.marginInlineStart = `${Math.min(Number(level) || 0, 4) * 16}px`
			item.textContent = content
			list.append(item)
			return
		}

		list = null

		if (type === "heading") {
			const depth = Math.min(Math.max(Number(level) || 2, 1), 6)
			const heading = document.createElement(`h${depth}`)
			heading.textContent = content
			wrapper.append(heading)
			return
		}

		const paragraph = document.createElement("p")
		paragraph.textContent = content
		wrapper.append(paragraph)
	})

	return wrapper
}

function buildNote(message: string): HTMLElement {
	const note = el("p", "pl-doc-preview__note")
	note.textContent = message
	return note
}

function el(tag: string, className: string): HTMLElement {
	const node = document.createElement(tag)
	node.className = className
	return node
}
